/**
 * Typed client for /api/notes/* — direct and group messaging.
 *
 * Mirrors hy_sales/schemas/notes.py — keep in lockstep. When the
 * backend Pydantic shapes change, update the interfaces here in the
 * same PR.
 *
 * A conversation is a THREAD with participants. A direct message is
 * just a thread with `is_group: false` and two people, so the UI has
 * one shape to render rather than a DM path and a group path.
 *
 * Read state is a per-participant watermark on the backend, not a flag
 * per message — so there is no "mark this message read", only "I have
 * opened this thread".
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiDelete, apiGet, apiPatch, apiPost } from './client';

// =====================================================================
// Types
// =====================================================================

/** A participant, flattened for display. */
export interface NoteParty {
  id: string;
  display_name: string;
  email: string;
}

/** The optional account a message is about — enough to render a chip. */
export interface NoteAccountRef {
  id: number;
  name: string;
  state_code: string | null;
}

/** `system` rows narrate membership changes and render as centred
 *  notes rather than chat bubbles. */
export type MessageKind = 'message' | 'system';

export interface ThreadMessage {
  id: number;
  thread_id: number;
  sender: NoteParty;
  account: NoteAccountRef | null;
  body: string;
  kind: MessageKind;
  created_at: string;
  /** Direct messages only — null in a group, where a partial
   *  "2 of 5 have read this" is noise rather than signal. */
  read_by_other: boolean | null;
}

export interface ThreadSummary {
  id: number;
  /** Server-computed display name: the group's title, the other
   *  person's name (DM), or a list of members (unnamed group). */
  title: string;
  is_group: boolean;
  participants: NoteParty[];
  /** null for a group created but not yet spoken in. */
  last_message: string | null;
  last_message_at: string | null;
  last_message_sender: NoteParty | null;
  last_message_from_me: boolean;
  unread_count: number;
}

export interface ThreadsResponse {
  threads: ThreadSummary[];
  unread_count: number;
}

export interface ThreadDetail {
  id: number;
  title: string;
  is_group: boolean;
  created_by: string | null;
  participants: NoteParty[];
  /** Oldest first — reading order for a transcript. */
  messages: ThreadMessage[];
  /** True when the caller may rename the group or remove others.
   *  Comes from the API so the UI doesn't reimplement the rule and
   *  then disagree with it. */
  can_manage: boolean;
}

export interface UnreadCountResponse {
  unread_count: number;
}

export interface NoteRecipient {
  id: string;
  display_name: string;
  email: string;
  /** True for addresses on a priority domain (HY staff). */
  is_priority: boolean;
}

export interface NoteRecipientsResponse {
  recipients: NoteRecipient[];
}

export interface CreateThreadPayload {
  participant_ids: string[];
  title?: string | null;
  body?: string | null;
}

export interface SendMessagePayload {
  threadId: number;
  body: string;
  account_id?: number | null;
  /** Who's sending, so the message can be painted into the transcript
   *  before the server answers. Omit to skip the optimistic render. */
  optimisticSender?: NoteParty;
}

/** Temporary ids are negative so they can never collide with a real
 *  BIGSERIAL, and so `id < 0` is a reliable "still sending" test. */
export function isPendingMessage(message: ThreadMessage): boolean {
  return message.id < 0;
}

// =====================================================================
// Query keys
// =====================================================================

/** Cache keys for this module's queries. Not exported: callers use the
 *  hooks, and a key shape leaking outside is how two components end up
 *  inventing slightly different keys for the same data. */
const notesKeys = {
  all: ['notes'] as const,
  threads: ['notes', 'threads'] as const,
  thread: (threadId: number) => ['notes', 'thread', threadId] as const,
  unread: ['notes', 'unread'] as const,
  recipients: ['notes', 'recipients'] as const,
};

// =====================================================================
// Reads
// =====================================================================

/**
 * The conversation list.
 *
 * Polled so a message in a thread you're not currently looking at
 * still surfaces in the list and its per-row badge.
 */
export function useThreads(): UseQueryResult<ThreadsResponse> {
  return useQuery({
    queryKey: notesKeys.threads,
    queryFn: () => apiGet<ThreadsResponse>('sales', '/api/notes/threads'),
    staleTime: 20_000,
    refetchInterval: 60_000,
  });
}

/**
 * One thread's participants and history.
 *
 * Polled faster than the list: while a thread is open the user is
 * actively conversing, and a reply arriving a minute late feels broken
 * in a chat UI in a way it doesn't in an inbox.
 */
export function useThread(threadId: number | null): UseQueryResult<ThreadDetail> {
  return useQuery({
    queryKey: notesKeys.thread(threadId ?? 0),
    queryFn: () => apiGet<ThreadDetail>('sales', `/api/notes/threads/${threadId}`),
    enabled: threadId !== null,
    staleTime: 10_000,
    refetchInterval: 20_000,
    // Switching back to a thread you've already opened shows it from
    // cache instantly and revalidates behind the scenes, rather than
    // flashing a skeleton over content we already have.
    placeholderData: (previous) => previous,
  });
}

export function useUnreadNoteCount(enabled = true): UseQueryResult<UnreadCountResponse> {
  return useQuery({
    queryKey: notesKeys.unread,
    queryFn: () => apiGet<UnreadCountResponse>('sales', '/api/notes/unread-count'),
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled,
  });
}

/** Users the current user can message. Rarely changes — cached long. */
export function useNoteRecipients(enabled = true): UseQueryResult<NoteRecipientsResponse> {
  return useQuery({
    queryKey: notesKeys.recipients,
    queryFn: () => apiGet<NoteRecipientsResponse>('sales', '/api/notes/recipients'),
    staleTime: 5 * 60_000,
    enabled,
  });
}

// =====================================================================
// Writes
// =====================================================================

/** Start a DM (one participant) or a group (two or more). */
export function useCreateThread(): UseMutationResult<ThreadDetail, Error, CreateThreadPayload> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateThreadPayload) =>
      apiPost<ThreadDetail>('sales', '/api/notes/threads', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notesKeys.threads });
    },
  });
}

/**
 * Send a message, painted into the transcript immediately.
 *
 * Without this the flow is: POST → await → invalidate → refetch the
 * whole thread → render. Two round-trips before your own words appear,
 * which reads as lag in a chat. Instead the message goes into the cache
 * synchronously with a temporary negative id, then the server's real
 * row replaces it in place — no refetch at all on the happy path.
 *
 * On failure the optimistic row is rolled back and the caller restores
 * the draft, so nothing is silently lost.
 */
export function useSendMessage(): UseMutationResult<
  ThreadMessage,
  Error,
  SendMessagePayload,
  { previous?: ThreadDetail; tempId: number }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ threadId, optimisticSender: _ignored, ...body }: SendMessagePayload) =>
      apiPost<ThreadMessage>('sales', `/api/notes/threads/${threadId}/messages`, body),

    onMutate: async ({ threadId, body, optimisticSender }) => {
      const key = notesKeys.thread(threadId);
      // Stop an in-flight poll from landing on top of the optimistic
      // write and wiping it.
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<ThreadDetail>(key);
      const tempId = -Date.now();

      if (previous && optimisticSender) {
        queryClient.setQueryData<ThreadDetail>(key, {
          ...previous,
          messages: [
            ...previous.messages,
            {
              id: tempId,
              thread_id: threadId,
              sender: optimisticSender,
              account: null,
              body,
              kind: 'message',
              created_at: new Date().toISOString(),
              // Unknown until the server answers; rendering "Sent"
              // before it has been would be a lie.
              read_by_other: null,
            },
          ],
        });
      }

      return { previous, tempId };
    },

    onError: (_err, { threadId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notesKeys.thread(threadId), context.previous);
      }
    },

    onSuccess: (message, { threadId }, context) => {
      // Swap the placeholder for the real row rather than refetching.
      queryClient.setQueryData<ThreadDetail>(notesKeys.thread(threadId), (current) => {
        if (!current) return current;
        const withoutTemp = current.messages.filter((m) => m.id !== context?.tempId);
        // Guard against a poll having already delivered this message.
        if (withoutTemp.some((m) => m.id === message.id)) {
          return { ...current, messages: withoutTemp };
        }
        return { ...current, messages: [...withoutTemp, message] };
      });
      // The list preview genuinely changed, so it does need refreshing.
      void queryClient.invalidateQueries({ queryKey: notesKeys.threads });
    },
  });
}

/** Opening a thread is the read event — there is no per-message read. */
export function useMarkThreadRead(): UseMutationResult<UnreadCountResponse, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId: number) =>
      apiPost<UnreadCountResponse>('sales', `/api/notes/threads/${threadId}/read`),
    onSuccess: (data) => {
      // The endpoint returns the remaining total, so seed the badge
      // directly instead of round-tripping for it.
      queryClient.setQueryData<UnreadCountResponse>(notesKeys.unread, data);
      void queryClient.invalidateQueries({ queryKey: notesKeys.threads });
    },
  });
}

export function useRenameThread(): UseMutationResult<
  ThreadDetail,
  Error,
  { threadId: number; title: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, title }) =>
      apiPatch<ThreadDetail>('sales', `/api/notes/threads/${threadId}`, { title }),
    onSuccess: (_t, { threadId }) => {
      void queryClient.invalidateQueries({ queryKey: notesKeys.thread(threadId) });
      void queryClient.invalidateQueries({ queryKey: notesKeys.threads });
    },
  });
}

export function useAddParticipants(): UseMutationResult<
  ThreadDetail,
  Error,
  { threadId: number; participant_ids: string[] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, participant_ids }) =>
      apiPost<ThreadDetail>('sales', `/api/notes/threads/${threadId}/participants`, {
        participant_ids,
      }),
    onSuccess: (_t, { threadId }) => {
      void queryClient.invalidateQueries({ queryKey: notesKeys.thread(threadId) });
      void queryClient.invalidateQueries({ queryKey: notesKeys.threads });
    },
  });
}

/** Remove someone (creator only) or leave the group yourself. */
export function useRemoveParticipant(): UseMutationResult<
  ThreadDetail,
  Error,
  { threadId: number; userId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, userId }) =>
      apiDelete<ThreadDetail>('sales', `/api/notes/threads/${threadId}/participants/${userId}`),
    onSuccess: (_t, { threadId }) => {
      void queryClient.invalidateQueries({ queryKey: notesKeys.thread(threadId) });
      void queryClient.invalidateQueries({ queryKey: notesKeys.threads });
    },
  });
}

// =====================================================================
// UI helpers
// =====================================================================

/**
 * Compact relative timestamp — "just now", "2h ago", "3d ago", then an
 * absolute date once it's older than a week (at which point "23d ago"
 * stops being easier to read than the date itself).
 */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days <= 7) return `${days}d ago`;

  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Clock time for a message bubble — "2:14 PM". */
export function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "Today" / "Yesterday" / "Mar 3" divider label for a calendar day. */
export function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** Initials for an avatar — "Ryan Ellis" → "RE". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return '?';
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
  if (!last) return first.slice(0, 2).toUpperCase();
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}
